import { useQuery } from '@apollo/client'
import UserContext from '@/context/UserContext'
import { parse, parseISO } from 'date-fns'
import {
  ALL_TERMINAL_ENTRIES,
  TODAYS_TERMINAL_ENTRIES,
} from '@/modules/terminal'
import { useContext, useMemo } from 'react'
import { format } from '@/utils/date-fns'
import { useIntervalValue, useNodes, useOnErrorAuto } from '.'
import { getNodes } from '@/utils/graphql'

export function useTerminal() {
  const user = useContext(UserContext)
  const onErrorAuto = useOnErrorAuto()

  const getTimeDifference = (timestamp: Date) => {
    const diff = new Date(
      new Date().getTime() -
        timestamp.getTime() +
        timestamp.getTimezoneOffset() * 60000
    )
    return format(diff, 'HH:mm')
  }

  const {
    data: todaysEntries,
    loading,
    error,
  } = useQuery(TODAYS_TERMINAL_ENTRIES, {
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    variables: { user: user.id },
    onError: onErrorAuto(),
  })

  const { data: lastEntries } = useQuery(ALL_TERMINAL_ENTRIES, {
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    /* The value last: 50 is a buffer for if for one godforsaken reason we get
    duplicate checkIns, we will be able to check them out anyway. Just to be
    safe. */
    variables: { user: user.id, last: 50 },
    onError: onErrorAuto(),
  })

  const latestEntry = useMemo(() => {
    if (todaysEntries === undefined || lastEntries === undefined) return null
    const entriesNotCheckedOut = getNodes(lastEntries.allTimeEntries).filter(
      entry => entry.datetimeEnd === null
    )

    return entriesNotCheckedOut[0] ?? null
  }, [todaysEntries, lastEntries])

  const lastCheckIn = useMemo(
    () => (latestEntry ? parseISO(latestEntry.datetimeStart) : new Date()),
    [latestEntry]
  )

  const timeSinceCheckIn = useIntervalValue(
    () => getTimeDifference(lastCheckIn),
    10000,
    !!latestEntry
  )

  const isOnNetwork = useMemo(
    () => todaysEntries !== undefined && todaysEntries.isOnTerminalNetwork,
    [todaysEntries]
  )

  const isWorking = useMemo(
    () => todaysEntries !== undefined && todaysEntries.isWorking,
    [todaysEntries]
  )

  const todaysTerminalEntries = useNodes(todaysEntries?.todaysTimeEntries)

  const todaysTotalWorkTime = useMemo(() => {
    if (todaysEntries === undefined) return '-'

    const totalWorkTime = todaysEntries.todaysTimeEntriesTotalWorkTime
    if (totalWorkTime.length < 6) return '-'

    return format(parse(totalWorkTime, 'HH:mm:ss', new Date()), 'HH:mm')
  }, [todaysEntries])

  return {
    user,
    lastCheckIn,
    timeSinceCheckIn,
    isWorking,
    isOnNetwork,
    latestEntry,
    todaysTerminalEntries,
    todaysTotalWorkTime,
    loading,
    error,
    salaryWorkTime: lastEntries?.user?.salaryWorkTime,
  }
}
